<template>
    <div class="bva-overlay" @click.self="$emit('close')">
        <div class="bva-modal">
            <h4 class="bva-title">
                {{ $t('dash.settings.variables.bulk-apply-title') }}
                <strong>{{ variable.name }}</strong>
            </h4>

            <input
                type="text"
                class="bva-value-input"
                v-model="value"
                :placeholder="$t('dash.settings.variables.value-placeholder')"
                ref="valueInput"
            >

            <div class="bva-pages-header">
                <Checkbox
                    :isChecked="allPagesState !== 'none'"
                    :isLine="allPagesState === 'partial'"
                    :add="selectAllPages"
                    :remove="deselectAllPages"
                    id="all"
                    prefixId="bulkApplyValue"
                />
                <span>{{ allPagesLabel }}</span>
            </div>

            <div class="bva-pages-list">
                <div class="bva-group" v-for="group in pageGroups" :key="group.id || 'ungrouped'">
                    <div class="bva-checkbox-row bva-group-header">
                        <Checkbox
                            :isChecked="group.state !== 'none'"
                            :isLine="group.state === 'partial'"
                            :add="() => selectGroupPages(group)"
                            :remove="() => deselectGroupPages(group)"
                            :id="'group-' + (group.id || 'ungrouped')"
                            prefixId="bulkApplyValue"
                        />
                        <span>{{ group.name }}</span>
                    </div>
                    <div class="bva-checkbox-row bva-page-row" v-for="page in group.pages" :key="page.id">
                        <Checkbox
                            :isChecked="selectedPageIds.includes(page.id)"
                            :add="() => addPage(page.id)"
                            :remove="() => removePage(page.id)"
                            :id="'page-' + page.id"
                            prefixId="bulkApplyValue"
                        />
                        <img v-if="networkIcon(page.network)" :src="networkIcon(page.network)" class="bva-network-icon">
                        <Tooltip
                            :text="page.name"
                            position="top"
                            :condition="(element) => element.querySelector('span') && element.querySelector('span').offsetWidth < element.querySelector('span').scrollWidth"
                            classCustom="bva-page-name-tooltip"
                        >
                            <span class="bva-page-name">{{ page.name }}</span>
                        </Tooltip>
                    </div>
                </div>
            </div>

            <div class="bva-actions">
                <button class="bva-btn-cancel" @click="$emit('close')">{{ $t('dash.settings.variables.cancel') }}</button>
                <button
                    class="bva-btn-submit"
                    :disabled="!selectedPageIds.length || applying"
                    @click="submit"
                >
                    {{ $t('dash.settings.variables.bulk-apply-confirm') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import VariablesMixin from '../../mixins/Variables.vue'
import NotifMixin from '../../mixins/Notif.vue'
import Checkbox from '../utils/checkbox.vue'
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
    name: 'bulk-apply-variable-value',

    components: { Checkbox, Tooltip },

    mixins: [VariablesMixin, NotifMixin],

    props: {
        variable: {
            type: Object,
            required: true
        },
        allPages: {
            type: Array,
            default: () => []
        },
        initialValue: {
            type: String,
            default: ''
        },
        // pages cochées par défaut à l'ouverture - toutes les pages si non précisé
        // (icône du header de colonne), une seule page d'origine sinon (icône sur
        // une cellule/valeur déjà renseignée)
        initialSelectedPageIds: {
            type: Array,
            default: null
        }
    },

    data() {
        return {
            value: this.initialValue,
            selectedPageIds: this.initialSelectedPageIds ? [...this.initialSelectedPageIds] : this.allPages.map(p => p.id),
            groups: [],
            applying: false
        }
    },

    computed: {
        // Pages groupées par les groupes de pages définis par l'utilisateur - une
        // page sans groupe atterrit dans un groupe "Sans groupe" en fin de liste.
        pageGroups() {
            const groupState = (pages) => {
                if (!pages.length) return 'none'
                const selectedCount = pages.filter(p => this.selectedPageIds.includes(p.id)).length
                return selectedCount === 0 ? 'none' : selectedCount === pages.length ? 'all' : 'partial'
            }

            const groupedPageIds = new Set()
            const groups = this.groups.map(group => {
                const pages = (group.pages || [])
                    .map(pageId => this.allPages.find(p => p.id === pageId))
                    .filter(Boolean)
                pages.forEach(p => groupedPageIds.add(p.id))
                return { id: group.id, name: group.name, pages, state: groupState(pages) }
            }).filter(group => group.pages.length)

            const ungroupedPages = this.allPages.filter(p => !groupedPageIds.has(p.id))
            if (ungroupedPages.length) {
                groups.push({
                    id: null,
                    name: this.$t('dash.settings.variables.bulk-apply-ungrouped'),
                    pages: ungroupedPages,
                    state: groupState(ungroupedPages)
                })
            }

            return groups
        },

        allPagesState() {
            if (!this.allPages.length) return 'none'
            const count = this.selectedPageIds.length
            if (count === 0) return 'none'
            if (count === this.allPages.length) return 'all'
            return 'partial'
        },

        // Tout décoché → "Tout sélectionner". Dès qu'au moins une page est cochée
        // (partiel, case en tiret, ou complet) → "Tout désélectionner".
        allPagesLabel() {
            return this.allPagesState === 'none'
                ? this.$t('dash.settings.variables.bulk-apply-select-all')
                : this.$t('dash.settings.variables.bulk-apply-uncheck-all')
        }
    },

    mounted() {
        // Groupes de pages définis par l'utilisateur : lecture du cache partagé
        // localStorage.groups (alimenté par Bots.vue/Pages.vue/Posts.vue/...).
        if (localStorage.groups) {
            try {
                this.groups = JSON.parse(localStorage.groups)
            } catch (e) {
                console.error('Invalid groups cache:', e)
            }
        }

        this.$nextTick(() => this.$refs.valueInput && this.$refs.valueInput.focus())
    },

    methods: {
        networkIcon(network) {
            try {
                return require(`../../../assets/img/iconsNetwork/ico-${network}-2x.png`)
            } catch (e) {
                return null
            }
        },

        selectAllPages() {
            this.selectedPageIds = this.allPages.map(p => p.id)
        },

        deselectAllPages() {
            this.selectedPageIds = []
        },

        selectGroupPages(group) {
            const ids = new Set(this.selectedPageIds)
            group.pages.forEach(p => ids.add(p.id))
            this.selectedPageIds = Array.from(ids)
        },

        deselectGroupPages(group) {
            const groupIds = new Set(group.pages.map(p => p.id))
            this.selectedPageIds = this.selectedPageIds.filter(id => !groupIds.has(id))
        },

        addPage(pageId) {
            if (!this.selectedPageIds.includes(pageId)) {
                this.selectedPageIds = [...this.selectedPageIds, pageId]
            }
        },

        removePage(pageId) {
            this.selectedPageIds = this.selectedPageIds.filter(id => id !== pageId)
        },

        async submit() {
            if (!this.selectedPageIds.length || this.applying) return

            this.applying = true

            const results = await Promise.all(this.selectedPageIds.map(pageId => this.saveVariableValue(this.variable.id, pageId, this.value)))
            await this.$store.dispatch('variables/fetchVariables')

            this.applying = false

            if (results.some(res => !res || !res.body || !res.body.success)) {
                this.notify({ text: 'dash.settings.variables.value-save-failed', color: 'danger' })
                return
            }

            this.$emit('applied')
            this.$emit('close')
        }
    }
}
</script>

<style lang="scss" scoped>
.bva-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.bva-modal {
    background: #fff;
    border-radius: 10px;
    padding: 20px;
    width: 360px;
    max-width: 90vw;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.bva-title {
    font-size: 14px;
    font-weight: 500;
    margin: 0;

    strong {
        font-weight: 700;
    }
}

.bva-value-input {
    border: 1px solid #e3e6ee;
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    outline: none;

    &:focus {
        border-color: #6b1f4d;
    }
}

.bva-pages-header {
    display: flex;
    align-items: center;
    gap: 8px;
    border-bottom: 1px solid #eef0f5;
    padding-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
}

.bva-pages-list {
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.bva-group {
    display: flex;
    flex-direction: column;
    gap: 2px;

    & + .bva-group {
        margin-top: 6px;
    }
}

.bva-group-header {
    font-weight: 500;
}

.bva-page-row {
    padding-left: 24px;
}

.bva-checkbox-row {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;

    img {
        flex-shrink: 0;
        width: 14px;
        height: 14px;
    }
}

.bva-page-name-tooltip {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.bva-page-name {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.bva-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;

    button {
        border: none;
        border-radius: 6px;
        padding: 8px 14px;
        font-size: 13px;
        cursor: pointer;
    }

    .bva-btn-submit {
        background: #6b1f4d;
        color: #fff;

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    .bva-btn-cancel {
        background: #f5f6fa;
        color: #333;
    }
}
</style>
